import React, { useState } from 'react';
import { analytics } from '../services/api';
import iconFabric from '../assets/fabric.png';
import iconProducts from '../assets/products.png';
import iconContracts from '../assets/contracts.png';
import iconPrice from '../assets/price.png';

const fmtMln = (price) => {
    if (price == null) return '—';
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(price);
};

const getBrandPrices = (clusters) => {
    const allContracts = clusters.flatMap(c => c.contracts ?? []).filter(c => c.price != null && c.folder);

    const mins = clusters.map(c => c.min_price).filter(v => v != null);
    const avgs = clusters.map(c => c.avg_price).filter(v => v != null);
    const maxs = clusters.map(c => c.max_price).filter(v => v != null);

    const minVal = mins.length ? Math.min(...mins) : null;
    const maxVal = maxs.length ? Math.max(...maxs) : null;

    // Ближайший контракт по цене к min/max
    const closest = (target) => {
        if (target == null || !allContracts.length) return null;
        return allContracts.reduce((best, c) =>
            Math.abs(c.price - target) < Math.abs(best.price - target) ? c : best
        );
    };

    return {
        min: minVal,
        minFolder: closest(minVal)?.folder ?? null,
        avg: avgs.length ? avgs.reduce((a, b) => a + b, 0) / avgs.length : null,
        max: maxVal,
        maxFolder: closest(maxVal)?.folder ?? null,
    };
};

const getLastContract = (clusters) => {
    const all = clusters.flatMap(c => c.contracts ?? []).filter(c => c.folder);
    if (!all.length) return null;
    return all.reduce((best, c) => (c.folder > best.folder ? c : best), all[0]);
};

const eisLink = (folder) =>
    `https://zakupki.gov.ru/epz/contract/contractCard/common-info.html?reestrNumber=${folder}`;

const PriceCell = ({ value, folder, className }) => {
    if (value == null) return <span className="kd-no-price">—</span>;
    if (folder) {
        return (
            <a href={eisLink(folder)} target="_blank" rel="noopener noreferrer" className={`kd-price-link ${className}`}>
                {fmtMln(value)}
            </a>
        );
    }
    return <span className={className}>{fmtMln(value)}</span>;
};

const PriceBar = ({ min, minFolder, avg, max, maxFolder }) => {
    if (min == null && avg == null && max == null) return <span className="kd-no-price">—</span>;
    return (
        <div className="kd-price-values">
            <PriceCell value={min} folder={minFolder} className="kd-price-min" />
            <span className="kd-price-avg">{fmtMln(avg)}</span>
            <PriceCell value={max} folder={maxFolder} className="kd-price-max" />
        </div>
    );
};

const BrandRow = ({ brand, rank }) => {
    const [expanded, setExpanded] = useState(false);
    const isLeader = rank === 1;
    const clusters = brand.product_clusters ?? [];
    const prices = getBrandPrices(clusters);
    const lastContract = getLastContract(clusters);

    const handleExpand = () => {
        if (!expanded) analytics.track('brand_expand', { brand: brand.brands });
        setExpanded(v => !v);
    };

    return (
        <>
            <tr className={`kd-row${isLeader ? ' kd-row-leader' : ''}`}>
                <td className="kd-td kd-td-rank">
                    <span className={`kd-rank-circle${isLeader ? ' kd-rank-leader' : ''}`}>{rank}</span>
                </td>
                <td className="kd-td kd-td-brand">
                    <div className="kd-brand-name">{brand.brands || 'Без бренда'}</div>
                    {isLeader && <span className="kd-leader-badge">Лидер по контрактам</span>}
                </td>
                <td className="kd-td kd-td-center kd-contracts-count">{brand.total_contracts}</td>
                <td className="kd-td kd-td-center kd-td-models">{clusters.length}</td>
                <td className="kd-td kd-td-price-bordered">
                    <PriceBar min={prices.min} minFolder={prices.minFolder} avg={prices.avg} max={prices.max} maxFolder={prices.maxFolder} />
                </td>
                <td className="kd-td kd-td-last">
                    {lastContract ? (
                        <a
                            className="kd-folder-link"
                            href={`https://zakupki.gov.ru/epz/contract/contractCard/common-info.html?reestrNumber=${lastContract.folder}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {lastContract.folder}
                        </a>
                    ) : <span className="kd-no-price">—</span>}
                    <button className="kd-details-btn" onClick={handleExpand}>
                        {expanded ? 'Скрыть' : 'Подробнее'} →
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr className="kd-expand-row">
                    <td colSpan={6} className="kd-expand-td">
                        <div className="kd-clusters-wrap">
                            {clusters.map((cluster, i) => (
                                <div key={i} className="kd-cluster">
                                    <div className="kd-cluster-name">{cluster.name}</div>
                                    <div className="kd-cluster-meta">
                                        <span className="kd-cluster-count">{cluster.contract_count} контр.</span>
                                        <span className="kd-cluster-prices">
                                            <span className="kd-price-min">{fmtMln(cluster.min_price)}</span>
                                            <span className="kd-cluster-sep">·</span>
                                            <span className="kd-price-avg">{fmtMln(cluster.avg_price)}</span>
                                            <span className="kd-cluster-sep">·</span>
                                            <span className="kd-price-max">{fmtMln(cluster.max_price)}</span>
                                        </span>
                                    </div>
                                    <div className="kd-cluster-contracts">
                                        {(cluster.contracts ?? []).slice(0, 5).map((c, j) => (
                                            <span key={j} className="kd-contract-chip">
                                                {c.folder ? (
                                                    <a
                                                        href={`https://zakupki.gov.ru/epz/contract/contractCard/common-info.html?reestrNumber=${c.folder}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="kd-folder-link kd-folder-chip-link"
                                                    >
                                                        {c.folder}
                                                    </a>
                                                ) : '—'}
                                                {c.price != null && <span className="kd-chip-price">{fmtMln(c.price)}</span>}
                                            </span>
                                        ))}
                                        {(cluster.contracts ?? []).length > 5 && (
                                            <span className="kd-more-contracts">+{cluster.contracts.length - 5} ещё</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const pluralModels = (n) => {
    if (n % 10 === 1 && n % 100 !== 11) return 'модель';
    if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return 'модели';
    return 'моделей';
};

const getPaginationPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
    if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
};

export const KtruSummary = ({ results }) => {
    const brands = results ?? [];
    const totalBrands = brands.length;
    const totalModels = brands.reduce((s, b) => s + (b.product_clusters?.length ?? 0), 0);
    const totalContracts = brands.reduce((s, b) => s + (b.total_contracts ?? 0), 0);

    const allClusters = brands.flatMap(b => b.product_clusters ?? []);
    const mins = allClusters.map(c => c.min_price).filter(v => v != null);
    const maxs = allClusters.map(c => c.max_price).filter(v => v != null);
    const globalMin = mins.length ? Math.min(...mins) : null;
    const globalMax = maxs.length ? Math.max(...maxs) : null;

    const stats = [
        { icon: iconFabric,    color: '#e8eeff', label: 'Производителей',    value: totalBrands.toLocaleString('ru-RU') },
        { icon: iconProducts,  color: '#e8f8ef', label: 'Товаров / моделей', value: totalModels.toLocaleString('ru-RU') },
        { icon: iconContracts, color: '#fff2e8', label: 'Контрактов',        value: totalContracts.toLocaleString('ru-RU') },
        {
            icon: iconPrice,
            color: '#f0eeff',
            label: 'Диапазон цен',
            value: globalMin != null && globalMax != null
                ? <>от {fmtMln(globalMin)}<br />до {fmtMln(globalMax)}</>
                : '—',
        },
    ];

    return (
        <div className="kd-summary">
            {stats.map((s, i) => (
                <div key={i} className="kd-summary-card">
                    <div className="kd-summary-icon" style={{ background: s.color }}>
                        <img src={s.icon} alt={s.label} />
                    </div>
                    <div className="kd-summary-text">
                        <div className="kd-summary-label">{s.label}</div>
                        <div className="kd-summary-value">{s.value}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const KTRU_PAGE_SIZE = 10;

const KtruAnalysis = ({ results }) => {
    // исключаем "Без бренда" из всех расчётов
    const brands = (results ?? []).filter(b => b.brands);

    const byContracts = [...brands].sort((a, b) => (b.total_contracts ?? 0) - (a.total_contracts ?? 0));
    const byModels    = [...brands].sort((a, b) => (b.product_clusters?.length ?? 0) - (a.product_clusters?.length ?? 0));

    const minPriceBrand = brands.reduce((best, b) => {
        const prices = (b.product_clusters ?? []).map(c => c.min_price).filter(v => v != null && v > 0);
        if (!prices.length) return best;
        const m = Math.min(...prices);
        return (!best || m < best.val) ? { val: m, name: b.brands } : best;
    }, null);

    const maxPriceBrand = brands.reduce((best, b) => {
        const prices = (b.product_clusters ?? []).map(c => c.max_price).filter(v => v != null);
        if (!prices.length) return best;
        const m = Math.max(...prices);
        return (!best || m > best.val) ? { val: m, name: b.brands } : best;
    }, null);

    const totalContracts = (results ?? []).reduce((s, b) => s + (b.total_contracts ?? 0), 0);

    const items = [
        {
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
            color: '#eff6ff',
            title: 'Самый частый производитель',
            value: byContracts[0] ? `${byContracts[0].brands} (${byContracts[0].total_contracts} контрактов)` : '—',
        },
        {
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
            color: '#f0fdf4',
            title: 'Самая низкая цена',
            value: minPriceBrand ? <><strong>{fmtMln(minPriceBrand.val)}</strong><br /><span className="kd-analysis-sub">{minPriceBrand.name}</span></> : '—',
        },
        {
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
            color: '#f5f3ff',
            title: 'Самый широкий ассортимент',
            value: byModels[0] ? `${byModels[0].brands} (${byModels[0].product_clusters?.length} моделей)` : '—',
        },
        {
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>,
            color: '#fff7ed',
            title: 'Самый дорогой диапазон',
            value: maxPriceBrand ? `${maxPriceBrand.name} (до ${fmtMln(maxPriceBrand.val)})` : '—',
        },
        {
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
            color: '#f0fdf4',
            title: 'Достаточно данных',
            value: <><strong>{totalContracts.toLocaleString('ru-RU')} контрактов</strong><br /><span className="kd-analysis-sub">для анализа рынка</span></>,
        },
    ];

    return (
        <div className="kd-analysis">
            <div className="kd-analysis-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                Краткий анализ
            </div>
            <div className="kd-analysis-items">
                {items.map((item, i) => (
                    <div key={i} className="kd-analysis-item">
                        <div className="kd-analysis-icon" style={{ background: item.color }}>{item.icon}</div>
                        <div className="kd-analysis-text">
                            <div className="kd-analysis-label">{item.title}</div>
                            <div className="kd-analysis-value">{item.value}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="kd-analysis-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Данные основаны на завершённых контрактах из ЕИС и включают реальные цены поставок.
            </div>
        </div>
    );
};

const KtruDashboard = ({ results, ktru }) => {
    const [shown, setShown] = useState(KTRU_PAGE_SIZE);
    const [sortAsc, setSortAsc] = useState(false);

    const sorted = [...(results ?? [])].sort((a, b) => {
        const aNoName = !a.brands;
        const bNoName = !b.brands;
        if (aNoName !== bNoName) return aNoName ? 1 : -1;
        const diff = (a.total_contracts ?? 0) - (b.total_contracts ?? 0);
        return sortAsc ? diff : -diff;
    });

    const paged = sorted.slice(0, shown);
    const remaining = sorted.length - shown;

    return (
        <div className="ktru-dashboard">
            <KtruSummary results={results} />
            <div className="kd-main-wrap">
                <div className="kd-table-col">
                    <div className="kd-table-wrap">
                        <table className="kd-table">
                            <thead>
                                <tr className="kd-thead-row">
                                    <th className="kd-th kd-th-rank"></th>
                                    <th className="kd-th kd-th-brand">Производитель</th>
                                    <th className="kd-th kd-th-center kd-th-contracts kd-th-sortable" onClick={() => setSortAsc(v => !v)}>
                                        Контрактов {sortAsc ? '↑' : '↓'}
                                    </th>
                                    <th className="kd-th kd-th-center kd-th-narrow">Товаров / моделей</th>
                                    <th className="kd-th kd-th-price kd-th-price-bordered">Цены (₽)</th>
                                    <th className="kd-th kd-th-last">Последний контракт</th>
                                </tr>
                                <tr className="kd-thead-price-hint">
                                    <td colSpan={4}></td>
                                    <td className="kd-td-price-bordered">
                                        <div className="kd-price-hint-row">
                                            <span className="kd-price-min">Мин.</span>
                                            <span className="kd-price-avg">Средняя</span>
                                            <span className="kd-price-max">Макс.</span>
                                        </div>
                                    </td>
                                    <td></td>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((brand, idx) => (
                                    <BrandRow key={idx} brand={brand} rank={idx + 1} />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {remaining > 0 && (
                        <div className="kd-show-more-wrap">
                            <button className="kd-show-more-btn" onClick={() => setShown(v => v + KTRU_PAGE_SIZE)}>
                                Показать ещё {Math.min(remaining, KTRU_PAGE_SIZE)} производителей
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                        </div>
                    )}
                </div>

                <KtruAnalysis results={results} />
            </div>
        </div>
    );
};

export default KtruDashboard;
